import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Table,
  Typography,
  Space,
  message,
  Select,
  Card,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllHierarchicalElements,
  getHierarchicalTree,
  createHierarchicalElement,
  updateHierarchicalElement,
  deleteHierarchicalElement,
} from './slice-hierarchical-element';
import type { ColumnsType } from 'antd/es/table';

const { TextArea } = Input;

interface HierarchicalElement {
  id?: number;
  parentElementKey?: string;
  key?: string;
  element: string;
  parentId?: number | null;
  description?: string;
  comment?: string;
  tags?: string; // Comma-separated list
  apis?: string; // Comma-separated list
  level?: number;
  children?: HierarchicalElement[];
}

const HierarchicalElementComponent: React.FC = () => {
  const dispatch = useDispatch();
  const { elements, treeData, loading, updateHierarchicalElementloading } = useSelector(
    (state: any) => state.hierarchicalElement
  );

  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<HierarchicalElement | null>(null);

  useEffect(() => {
    loadTreeData();
  }, []);

  const loadTreeData = () => {
    dispatch(getHierarchicalTree() as any);
  };

  // Process tree data to add keys and ensure proper structure
  const processTreeData = (tree: HierarchicalElement[], parentElementKey: string = ''): HierarchicalElement[] => {
    return tree.map((node) => {
      const processedNode: HierarchicalElement = {
        ...node,
        parentElementKey: parentElementKey,
        key: node.id?.toString() || '',
      };
      
      if (node.children && node.children.length > 0) {
        processedNode.children = processTreeData(node.children,
          parentElementKey ? parentElementKey + '-' + node.element.toLowerCase().replaceAll(' ', '-') : node.element.toLowerCase().replaceAll(' ', '-')
        );
      }
      
      return processedNode;
    });
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ parentId: null });
    setIsModalVisible(true);
  };

  const handleEdit = (record: HierarchicalElement) => {
    setEditingRecord(record);
    form.setFieldsValue({
      id: record.id,
      element: record.element,
      parentId: record.parentId,
      description: record.description,
      comment: record.comment,
      tags: record.tags,
      apis: record.apis,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await dispatch(deleteHierarchicalElement(id) as any);
      message.success('Element deleted successfully');
      loadTreeData();
    } catch (error: any) {
      message.error(error?.message || 'Failed to delete element');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: HierarchicalElement = {
        element: values.element,
        parentId: values.parentId || null,
        description: values.description,
        comment: values.comment,
        tags: values.tags,
        apis: values.apis,
      };

      if (editingRecord?.id) {
        payload.id = editingRecord.id;
        await dispatch(updateHierarchicalElement(payload) as any);
        message.success('Element updated successfully');
      } else {
        await dispatch(createHierarchicalElement(payload) as any);
        message.success('Element created successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      loadTreeData();
    } catch (error: any) {
      if (error?.errorFields) {
        // Form validation errors
        return;
      }
      message.error(error?.message || 'Failed to save element');
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingRecord(null);
  };

  // Build parent options for select dropdown
  const buildParentOptions = (
    tree: HierarchicalElement[],
    excludeId?: number,
    level: number = 0,
    prefix: string = ''
  ): { value: number; label: string }[] => {
    const options: { value: number; label: string }[] = [];
    
    tree.forEach((node) => {
      if (node.id !== excludeId && level < 9) {
        // Limit to 9 levels (so adding child makes it 10)
        const label = prefix + node.element;
        options.push({ value: node.id!, label });
        
        if (node.children && node.children.length > 0) {
          const childOptions = buildParentOptions(
            node.children,
            excludeId,
            level + 1,
            prefix + '  '
          );
          options.push(...childOptions);
        }
      }
    });
    
    return options;
  };

  const tableData = treeData && treeData.length > 0 
    ? processTreeData(treeData) 
    : [];

  const parentOptions = treeData && treeData.length > 0
    ? [{ value: null, label: 'Root (No Parent)' }, ...buildParentOptions(treeData, editingRecord?.id)]
    : [{ value: null, label: 'Root (No Parent)' }];

  const columns: ColumnsType<HierarchicalElement> = [
    {
      title: 'Element',
      dataIndex: 'element',
      key: 'element',
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true, 
    },    {
      title: 'Element Key',
      dataIndex: 'parentElementKey',
      key: 'parentElementKey',
      render: (text: string, record: HierarchicalElement) => 
        <span onClick={() => navigator.clipboard.writeText(record?.parentElementKey ? record.parentElementKey + '-' + record.element.toLowerCase().replaceAll(' ', '-') : record.element.toLowerCase().replaceAll(' ', '-'))}>{record?.parentElementKey ? record.parentElementKey + '-' + record.element.toLowerCase().replaceAll(' ', '-') : record.element.toLowerCase().replaceAll(' ', '-')}</span>
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      ellipsis: true,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      ellipsis: true,
      render: (tags: string) => tags || '-',
    },
    {
      title: 'APIs',
      dataIndex: 'apis',
      key: 'apis',
      ellipsis: true,
      render: (apis: string) => apis || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: HierarchicalElement) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this element?"
            onConfirm={() => record.id && handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Hierarchical Elements"
      extra={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadTreeData}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add Element
          </Button>
        </Space>
      }
    >
      <Table
        columns={columns}
        dataSource={tableData}
        loading={loading}
        pagination={{ pageSize: 50 }}
        rowKey="id"
        defaultExpandAllRows={false}
      />

      <Modal
      
        title={editingRecord ? 'Edit Element' : 'Add Element'}
        open={isModalVisible}
        onOk={handleSubmit}
        //loading={updateHierarchicalElementloading}
        onCancel={handleCancel}
        okButtonProps={{ loading: updateHierarchicalElementloading }}
        cancelButtonProps={{ loading: updateHierarchicalElementloading }}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          disabled={updateHierarchicalElementloading}
          layout="vertical"
          initialValues={{ parentId: null }}
        >
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="element"
            label="Element"
            rules={[
              { required: true, message: 'Please enter element name' },
              { max: 255, message: 'Element name must be less than 255 characters' },
            ]}
          >
            <Input placeholder="Enter element name" />
          </Form.Item>

          <Form.Item
            name="parentId"
            label="Parent Element"
            tooltip="Select parent element. Leave empty for root level. Maximum 10 levels supported."
          >
            <Select
              placeholder="Select parent (optional)"
              allowClear
              showSearch
              optionFilterProp="label"
              options={parentOptions}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 1000, message: 'Description must be less than 1000 characters' }]}
          >
            <TextArea
              rows={3}
              placeholder="Enter description"
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="comment"
            label="Comment"
            rules={[{ max: 2000, message: 'Comment must be less than 2000 characters' }]}
          >
            <TextArea
              rows={3}
              placeholder="Enter comment"
              maxLength={2000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="tags"
            label="Tags"
            tooltip="Enter comma-separated tags (e.g., tag1, tag2, tag3)"
            rules={[{ max: 2000, message: 'Tags must be less than 2000 characters' }]}
          >
            <Input
              placeholder="Enter comma-separated tags"
              maxLength={2000}
            />
          </Form.Item>

          <Form.Item
            name="apis"
            label="APIs"
            tooltip="Enter comma-separated APIs (e.g., /api/users, /api/orders, /api/products)"
            rules={[{ max: 2000, message: 'APIs must be less than 2000 characters' }]}
          >
            <Input
              placeholder="Enter comma-separated APIs"
              maxLength={2000}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default HierarchicalElementComponent;



